import { useCallback, useEffect, useState } from 'react';

import {
  getBillingStatus,
  refreshBillingStatus,
  type BillingStatusResponse,
} from './billingApi';

type BillingPanelProps = {
  onModulesChanged?: (modules: BillingStatusResponse['modules']) => void;
};

function statusLabel(value: string): string {
  return value.replaceAll('_', ' ').toLowerCase();
}

function subscriptionSummary(value: BillingStatusResponse): string {
  if (!value.configured) return 'Shopify App Pricing connection is not configured yet.';
  if (value.subscription.state === 'unknown') return 'Subscription has not been verified yet.';
  if (value.subscription.state === 'none') return 'No active Shopify app subscription.';
  if (value.subscription.state === 'trialing') return 'Shopify subscription trial is active.';
  return 'Shopify subscription is active.';
}

export function BillingPanel({ onModulesChanged }: BillingPanelProps) {
  const [status, setStatus] = useState<BillingStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyStatus = useCallback(
    (value: BillingStatusResponse) => {
      setStatus(value);
      onModulesChanged?.(value.modules);
    },
    [onModulesChanged],
  );

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      applyStatus(await refreshBillingStatus());
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not verify Shopify subscription.');
    } finally {
      setRefreshing(false);
    }
  }, [applyStatus]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getBillingStatus()
      .then(async (value) => {
        if (!active) return;
        applyStatus(value);

        const returnedFromPricing = new URLSearchParams(window.location.search).has('plan_handle');
        const needsVerification = value.configured && value.stale;
        if (returnedFromPricing || needsVerification) {
          try {
            const live = await refreshBillingStatus();
            if (active) applyStatus(live);
          } catch (reason: unknown) {
            if (active) {
              setError(
                reason instanceof Error
                  ? reason.message
                  : 'Could not verify Shopify subscription.',
              );
            }
          }
        }
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setError(reason instanceof Error ? reason.message : 'Could not load module status.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [applyStatus]);

  const openPlans = useCallback(() => {
    if (!status?.manage_plan_url) return;
    window.open(status.manage_plan_url, '_top');
  }, [status?.manage_plan_url]);

  return (
    <s-section heading="Modules & plan">
      <s-stack direction="block" gap="base">
        <s-paragraph>
          Shopify manages subscription selection and billing. Atellius mirrors the verified
          subscription and activates only the modules mapped to that contract.
        </s-paragraph>

        {loading && <s-paragraph>Loading subscription and module access…</s-paragraph>}

        {!loading && status && (
          <>
            <s-heading>{subscriptionSummary(status)}</s-heading>
            {status.stale && status.configured && (
              <s-paragraph>
                Cached billing evidence is stale. Paid billing-owned modules remain locked until
                Shopify subscription verification succeeds.
              </s-paragraph>
            )}
            {status.subscription.billing_period && (
              <s-paragraph>
                Billing period: {statusLabel(status.subscription.billing_period)}
                {status.subscription.cancel_at_end_of_cycle
                  ? ' · scheduled to cancel at the end of the current cycle'
                  : ''}
              </s-paragraph>
            )}
            {status.subscription.trial_ends_at && (
              <s-paragraph>Trial ends: {status.subscription.trial_ends_at}</s-paragraph>
            )}
            {status.subscription.synced_at && (
              <s-paragraph>Last verified with Shopify: {status.subscription.synced_at}</s-paragraph>
            )}

            {status.attention && <s-paragraph>Needs attention: {status.attention}</s-paragraph>}
            {status.module_mapping.unknown_item_handles.length > 0 && (
              <s-paragraph>
                Unmapped Shopify billing item(s):{' '}
                {status.module_mapping.unknown_item_handles.join(', ')}. No module is granted by an
                unknown billing handle.
              </s-paragraph>
            )}

            <s-heading>Atellius access</s-heading>
            {status.modules.map((module) => (
              <s-stack key={module.code} direction="block" gap="small">
                <s-heading>{module.name}</s-heading>
                <s-paragraph>
                  {module.enabled ? 'Enabled' : 'Not enabled'} · {statusLabel(module.status)}
                  {module.source ? ` · source ${statusLabel(module.source)}` : ''}
                  {!module.billable ? ' · included with Core' : ''}
                </s-paragraph>
              </s-stack>
            ))}

            <s-stack direction="inline" gap="small">
              <s-button onClick={() => void refresh()} disabled={refreshing || !status.configured}>
                {refreshing ? 'Checking Shopify…' : 'Refresh subscription'}
              </s-button>
              <s-button onClick={openPlans} disabled={!status.manage_plan_url}>
                Manage plan in Shopify
              </s-button>
            </s-stack>
          </>
        )}

        {error && (
          <s-paragraph>
            Billing verification needs attention: {error} Existing module access was not changed by
            this failed verification.
          </s-paragraph>
        )}
      </s-stack>
    </s-section>
  );
}
